import { useEffect, useRef, useState } from 'react';
import { useWhatsAppLead } from '@/hooks/useWhatsAppLead';

type CellValue = boolean | string;

interface CompareRow {
  category: string;
  features: { label: string; vitrin: CellValue; satis: CellValue; pro: CellValue }[];
}

const compareRows: CompareRow[] = [
  {
    category: 'Temel Özellikler',
    features: [
      { label: 'Tanıtım / Hizmet Sayfası', vitrin: true, satis: true, pro: true },
      { label: 'Mobil Uyumlu Tasarım', vitrin: true, satis: true, pro: true },
      { label: 'WhatsApp Butonu', vitrin: true, satis: true, pro: true },
      { label: 'İletişim Formu', vitrin: true, satis: true, pro: true },
      { label: 'Google Maps Entegrasyonu', vitrin: true, satis: true, pro: true },
      { label: 'Çoklu Hizmet Sayfası', vitrin: false, satis: true, pro: true },
    ],
  },
  {
    category: 'Satış & Dönüşüm',
    features: [
      { label: 'Funnel Yapısı (Akıllı Yönlendirme)', vitrin: false, satis: 'Temel', pro: 'Gelişmiş' },
      { label: 'Kampanya Sayfaları', vitrin: false, satis: true, pro: true },
      { label: 'Lead / Form Toplama', vitrin: false, satis: 'Temel', pro: 'Gelişmiş' },
      { label: 'A/B Test Sayfaları', vitrin: false, satis: false, pro: true },
    ],
  },
  {
    category: 'Analiz & Takip',
    features: [
      { label: 'Temel Analiz & Raporlama', vitrin: false, satis: true, pro: true },
      { label: 'Gelişmiş Davranış Analizi', vitrin: false, satis: false, pro: true },
      { label: 'Adım Adım Kullanıcı Takibi', vitrin: false, satis: false, pro: true },
      { label: 'Kampanya Bazlı Performans', vitrin: false, satis: false, pro: true },
    ],
  },
  {
    category: 'Reklam Yönetimi',
    features: [
      { label: 'Reklam Entegrasyonu Desteği', vitrin: false, satis: true, pro: true },
      { label: 'Tek Panelden Reklam Siparişi', vitrin: false, satis: false, pro: true },
      { label: 'Reklam Akışı Yönetim Modülü', vitrin: false, satis: false, pro: true },
    ],
  },
  {
    category: 'Destek & Kurulum',
    features: [
      { label: 'Kurulum Süresi', vitrin: '24 saat', satis: '24 saat', pro: '24 saat' },
      { label: 'Destek', vitrin: 'Standart', satis: 'Öncelikli', pro: 'Öncelikli' },
      { label: 'Özel Entegrasyonlar', vitrin: false, satis: false, pro: true },
      { label: 'Yönetim Paneli', vitrin: false, satis: false, pro: true },
    ],
  },
];

function CellIcon({ value }: { value: CellValue }) {
  if (value === true) {
    return (
      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-green-500/15 border border-green-500/25 mx-auto">
        <i className="ri-check-line text-green-400 text-xs" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/5 mx-auto">
        <i className="ri-subtract-line text-gray-600 text-xs" />
      </span>
    );
  }
  return <span className="text-xs font-semibold text-gold">{value}</span>;
}

const packages = [
  {
    id: 'vitrin',
    name: 'Smart Vitrin',
    tagline: 'Dijital varlık için mükemmel başlangıç',
    topTag: null as string | null,
    highlightText: null as string | null,
    differentiator: null as string | null,
    price: '500',
    highlight: false,
    color: 'border-white/10',
    accentColor: 'text-gray-300',
    priceColor: 'text-white',
    btnClass: 'btn-outline-neon',
    btnSubtext: null as string | null,
    badge: null as string | null,
    trustNote: 'Kurulum: 24 saat · İptal: İstediğin zaman',
    features: [
      'Temel tanıtım sayfası',
      'WhatsApp entegrasyonu',
      'Hızlı kurulum (24 saat)',
      'Mobil uyumlu tasarım',
      'İletişim formu',
      'Google Maps entegrasyonu',
    ],
    proBox: null as { stat: string; desc: string } | null,
  },
  {
    id: 'satis',
    name: 'Smart Satış Platformu',
    tagline: 'Müşteri getiren tam sistem',
    topTag: null as string | null,
    highlightText: null as string | null,
    differentiator: 'Kampanya + funnel + dönüşüm odaklı yapı',
    price: '1.000',
    highlight: true,
    color: 'border-gold/40',
    accentColor: 'text-gold',
    priceColor: 'text-gold',
    btnClass: 'btn-gold',
    btnSubtext: null as string | null,
    badge: 'En Çok Tercih Edilen',
    trustNote: 'Kurulum: 24 saat · İptal: İstediğin zaman',
    features: [
      'Çoklu hizmet sayfaları',
      'Temel funnel yapı (akıllı yönlendirme)',
      'Kampanya sayfaları',
      'Form ve lead toplama',
      'Temel analiz & raporlama',
      'WhatsApp & Telefon butonu',
      'Reklam entegrasyonu desteği',
      'Öncelikli destek',
    ],
    proBox: null,
  },
  {
    id: 'pro',
    name: 'Smart Pro Platform',
    tagline: 'Satış + Analiz + Reklam — tek panelde',
    topTag: 'En Güçlü Sistem',
    highlightText: 'Hem ziyaretçinizi analiz edin hem istediğiniz zaman reklam başlatın.',
    differentiator: null as string | null,
    price: '2.000',
    highlight: false,
    color: 'border-neon/20',
    accentColor: 'text-neon',
    priceColor: 'text-neon',
    btnClass: 'btn-outline-neon',
    btnSubtext: 'En hızlı büyüyen işletmeler bunu kullanıyor',
    badge: null as string | null,
    trustNote: 'Kurulum: 24 saat · İptal: İstediğin zaman',
    features: [
      'Gelişmiş davranış analizi',
      'Adım adım kullanıcı takibi',
      'Kampanya bazlı detaylı performans',
      'Tek panelden reklam siparişi',
      'Reklam akışı yönetim modülü',
      'Gelişmiş lead toplama sistemi',
      'A/B test sayfaları',
      'Özel entegrasyonlar & yönetim paneli',
    ],
    proBox: {
      stat: '%34 daha fazla dönüşüm',
      desc: 'Bu paketi alanlar reklam + platform birlikte kullanır ve ortalama %34 daha fazla dönüşüm alır.',
    },
  },
];

const WHATSAPP_NUMBER = '908508408383';

function getDemoWhatsAppUrl(packageName: string): string {
  const message = encodeURIComponent(
    `Merhaba, "${packageName}" paketi için demo talep ediyorum.`
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
}

export default function PackagesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const { openModal } = useWhatsAppLead();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.pkg-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="packages"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: '#0d0d15' }}
    >
      <div className="absolute top-0 left-0 right-0 section-divider pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(ellipse at 50% 100%, #f0b429, transparent 60%)' }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8" ref={ref}>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 px-4 py-2 rounded-full mb-5">
            <i className="ri-price-tag-3-line text-gold text-sm" />
            <span className="text-gold text-sm font-semibold">Paketler</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            İşletmenize Uygun{' '}
            <span className="text-gold-gradient">Paketi Seçin</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Her paket, daha fazla müşteri getirmek için tasarlanmıştır. Hepsi aynı kalite, farklı güç.
          </p>
        </div>

        {/* Mini açıklama */}
        <div className="text-center mb-14">
          <p className="text-gray-300 text-base max-w-2xl mx-auto leading-relaxed border border-white/8 rounded-2xl px-6 py-4 inline-block"
            style={{ background: 'rgba(255,255,255,0.03)' }}>
            İhtiyacınıza göre sadece vitrin değil,{' '}
            <span className="text-white font-semibold">müşteri getiren ve yönetilebilen satış platformları</span>{' '}
            kuruyoruz.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {packages.map((pkg, idx) => (
            <div
              key={pkg.id}
              className={`pkg-card relative rounded-3xl p-7 border transition-all duration-300 cursor-pointer ${pkg.color} ${
                pkg.highlight
                  ? 'glass-gold md:-mt-4 md:pb-11'
                  : 'glass'
              } ${hovered === pkg.id ? 'scale-[1.02]' : ''}`}
              style={{
                opacity: 0,
                transform: 'translateY(40px)',
                transition: 'all 0.5s ease',
                ...(pkg.highlight
                  ? { boxShadow: '0 0 60px rgba(240,180,41,0.18), 0 0 120px rgba(240,180,41,0.06)' }
                  : {}),
              }}
              onMouseEnter={() => setHovered(pkg.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Badge */}
              {pkg.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="btn-gold px-5 py-1.5 rounded-full text-xs font-black animate-glow">
                    ⭐ {pkg.badge}
                  </span>
                </div>
              )}

              {/* Package header */}
              <div className="mb-5">
                {/* Top tag */}
                {pkg.topTag ? (
                  <div className="inline-flex items-center gap-1.5 bg-neon/10 border border-neon/25 px-3 py-1 rounded-full mb-3">
                    <i className="ri-flashlight-line text-neon text-xs" />
                    <span className="text-neon text-xs font-black uppercase tracking-wider">{pkg.topTag}</span>
                  </div>
                ) : (
                  <div className={`text-xs font-black uppercase tracking-widest mb-2 ${pkg.accentColor} opacity-70`}>
                    {pkg.highlight ? '● ÖNERILEN' : idx === 0 ? '○ BAŞLANGIC' : '◆ KURUMSAL'}
                  </div>
                )}

                <h3 className="text-2xl font-extrabold text-white mb-1">{pkg.name}</h3>
                <p className="text-gray-400 text-sm">{pkg.tagline}</p>

                {/* Differentiator — Satış kartı için */}
                {pkg.differentiator && (
                  <p className="text-xs text-gray-400 mt-1.5 italic">{pkg.differentiator}</p>
                )}

                {/* Pro highlight box */}
                {pkg.highlightText && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl px-3.5 py-2.5 border border-neon/20"
                    style={{ background: 'rgba(0,212,255,0.06)' }}>
                    <i className="ri-rocket-2-line text-neon text-sm flex-shrink-0 mt-0.5" />
                    <span className="text-neon text-xs font-semibold leading-snug">
                      {pkg.highlightText}
                    </span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className={`flex items-end gap-1.5 mb-2 ${pkg.highlight ? 'pb-1' : ''}`}>
                <span className={`text-5xl md:text-6xl font-black tracking-tight leading-none ${pkg.priceColor}`}>
                  {pkg.price}
                </span>
                <div className="flex flex-col mb-1.5">
                  <span className="text-gray-400 text-sm font-semibold leading-tight">TL</span>
                  <span className="text-gray-500 text-xs leading-tight">/ Ay</span>
                </div>
              </div>

              {/* Trust note */}
              <p className="text-gray-500 text-xs mb-5">
                {pkg.trustNote}
              </p>

              <div className="h-px bg-white/5 mb-6" />

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {pkg.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-3 text-sm">
                    <span className={`w-5 h-5 flex items-center justify-center rounded-full ${pkg.highlight ? 'bg-gold/20 text-gold' : 'bg-neon/10 text-neon'} flex-shrink-0 mt-0.5`}>
                      <i className="ri-check-line text-xs" />
                    </span>
                    <span className="text-gray-300">{feat}</span>
                  </li>
                ))}
              </ul>

              {/* Pro psikolojik kutu */}
              {pkg.proBox && (
                <div className="mb-6 rounded-xl border border-neon/15 px-4 py-3.5"
                  style={{ background: 'rgba(0,212,255,0.04)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-bar-chart-2-line text-neon text-sm" />
                    <span className="text-neon text-sm font-black">{pkg.proBox.stat}</span>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      'Reklam + platform birlikte kullanır',
                      'Ortalama %34 daha fazla dönüşüm alır'
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="text-neon">→</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={() => openModal(`Paket: ${pkg.name}`)}
                className={`w-full py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${pkg.btnClass}`}
              >
                <i className="ri-whatsapp-line text-base" />
                Demo Talep Et
              </button>

              {/* Button subtext */}
              {pkg.btnSubtext && (
                <p className="text-center text-gray-500 text-xs mt-2">({pkg.btnSubtext})</p>
              )}
            </div>
          ))}
        </div>

        {/* Comparison Table Toggle */}
        <div className="mt-14">
          <div className="text-center mb-6">
            <button
              type="button"
              onClick={() => setShowTable(!showTable)}
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/10 text-sm font-semibold text-gray-300 hover:text-white hover:border-white/20 transition-all duration-200 cursor-pointer pointer-events-auto"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            >
              <i className={`ri-table-line text-gold text-base`} />
              {showTable ? 'Karşılaştırmayı Gizle' : 'Tüm Özellikleri Karşılaştır'}
              <i className={`text-xs text-gray-500 transition-transform duration-300 ${showTable ? 'ri-arrow-up-s-line rotate-0' : 'ri-arrow-down-s-line'}`} />
            </button>
          </div>

          {/* Table */}
          <div
            className="overflow-hidden transition-all duration-500 ease-in-out"
            style={{ maxHeight: showTable ? '2000px' : '0px', opacity: showTable ? 1 : 0 }}
          >
            <div className="rounded-2xl border border-white/8 overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
              {/* Table Header */}
              <div className="grid grid-cols-4 border-b border-white/8">
                <div className="px-5 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Özellik</div>
                {[
                  { name: 'Smart Vitrin', color: 'text-gray-300', price: '500 TL' },
                  { name: 'Smart Satış', color: 'text-gold', price: '1.000 TL', highlight: true },
                  { name: 'Smart Pro', color: 'text-neon', price: '2.000 TL' },
                ].map((pkg) => (
                  <div
                    key={pkg.name}
                    className={`px-4 py-4 text-center border-l border-white/8 ${pkg.highlight ? 'bg-gold/5' : ''}`}
                  >
                    <p className={`text-sm font-extrabold ${pkg.color}`}>{pkg.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{pkg.price} / Ay</p>
                  </div>
                ))}
              </div>

              {/* Rows */}
              {compareRows.map((group, gi) => (
                <div key={group.category}>
                  {/* Category header */}
                  <div className="grid grid-cols-4 border-b border-white/5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="col-span-4 px-5 py-2.5 flex items-center gap-2">
                      <i className="ri-folder-line text-gold text-xs" />
                      <span className="text-xs font-black text-gold uppercase tracking-widest">{group.category}</span>
                    </div>
                  </div>

                  {/* Feature rows */}
                  {group.features.map((row, ri) => (
                    <div
                      key={row.label}
                      className={`grid grid-cols-4 border-b border-white/5 transition-colors hover:bg-white/[0.02] ${
                        ri === group.features.length - 1 && gi === compareRows.length - 1 ? 'border-b-0' : ''
                      }`}
                    >
                      <div className="px-5 py-3.5 text-sm text-gray-400 flex items-center">{row.label}</div>
                      <div className="px-4 py-3.5 flex items-center justify-center border-l border-white/5">
                        <CellIcon value={row.vitrin} />
                      </div>
                      <div className="px-4 py-3.5 flex items-center justify-center border-l border-white/5 bg-gold/[0.03]">
                        <CellIcon value={row.satis} />
                      </div>
                      <div className="px-4 py-3.5 flex items-center justify-center border-l border-white/5">
                        <CellIcon value={row.pro} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div className="text-center mt-10 space-y-2">
          <p className="text-gray-400 text-sm">
            Fiyatlara <strong className="text-white">KDV dahil değildir.</strong> Yıllık ödeme seçeneğinde <span className="text-gold font-semibold">2 ay ücretsiz</span> fırsatından yararlanın.
          </p>
          <p className="text-gray-500 text-xs">
            Özel ihtiyaçlarınız için demo talep edin — işletmenize özel teklif hazırlıyoruz.
          </p>
        </div>
      </div>
    </section>
  );
}
